import Vue from "vue";
import Router from "vue-router";
import pipeline from "./pipeline.js";
import permissions from "@/router/middleware/permissions";
import intercom from "@/router/middleware/intercom";
import UserQuery from "@/graphql/queries/User";

Vue.use(Router);

const router = new Router({
	mode: "history",
	base: process.env.BASE_URL,
	routes: [
		{
			path: "/",
			name: "schools",
			component: () => import("@/views/schools/Schools.vue")
		},
		{
			path: "/overview",
			name: "overview",
			component: () => import("@/views/company/Overview.vue")
		},
		{
			path: "/referrals",
			name: "referrals",
			component: () => import("@/views/referrals/Referrals.vue")
		},
		{
			path: "/referrals/:referral",
			name: "referrals.show",
			component: () => import("@/views/referral/Referral.vue")
		},
		{
			path: "/activity",
			name: "activity",
			component: () => import("@/views/activity/Activity.vue")
		},
		{
			path: "/settings",
			name: "settings",
			component: () => import("@/views/settings/Settings.vue"),
			children: [
				{
					path: "account",
					name: "settings.account",
					component: () => import("@/views/settings/AccountSettings.vue")
				},
				{
					path: "team",
					name: "settings.team",
					component: () => import("@/views/settings/TeamSettings.vue"),
					meta: {
						permissions: ["crud-team"]
					}
				},
				{
					path: "tokens",
					name: "settings.tokens",
					component: () => import("@/views/settings/TokenSettings.vue"),
					meta: {
						permissions: ["create-token"]
					}
				},
				{
					path: "webhooks",
					name: "settings.webhooks",
					component: () => import("@/views/settings/WebhookSettings.vue")
				},
				{
					path: "scopes",
					name: "settings.scopes",
					component: () => import("@/views/settings/ScopeSettings.vue"),
					meta: {
						permissions: ["modify-scopes"]
					}
				}
			]
		},
		{
			path: "/schools/:school",
			component: () => import("@/views/school/School.vue"),
			children: [
				{
					path: "",
					name: "schools.show",
					component: () => import("@/views/school-details/Details.vue"),
					meta: {
						permissions: ["view-school"]
					}
				},
				{
					path: "access",
					name: "schools.show.access",
					component: () => import("@/views/school-access/Access.vue")
				},
				{
					path: "access-logs",
					name: "schools.show.access-logs",
					component: () => import("@/views/school-access-log/AccessLog.vue")
				},
				{
					path: "writebacks",
					name: "schools.show.writebacks",
					component: () => import("@/views/school-writebacks/Writebacks.vue"),
					meta: {
						permissions: ["view-writebacks"]
					}
				},
				{
					path: "tokens",
					name: "schools.show.tokens",
					component: () => import("@/views/school-tokens/Tokens.vue"),
					meta: {
						permissions: ["create-token"]
					}
				},
				{
					path: "reports",
					name: "schools.show.reports",
					component: () => import("@/views/school-reports/Reports.vue"),
					meta: {
						permissions: ["view-reports"]
					}
				},
				{
					path: "scopes",
					name: "schools.show.scopes",
					component: () => import("@/views/school-scopes/Scopes.vue")
				},
				{
					path: "activity",
					name: "schools.show.activity",
					component: () => import("@/views/school-activity/Activity.vue")
				}
			]
		},
		{
			path: "/writebacks",
			name: "writebacks",
			component: () => import("@/views/writebacks/Writebacks.vue"),
			meta: {
				permissions: ["view-writebacks"]
			}
		},
		{
			path: "/auth/login",
			name: "auth.login",
			component: () => import("@/views/auth/Login.vue"),
			meta: {
				layout: "auth"
			}
		},
		{
			path: "/auth/signin",
			name: "auth.force-login",
			component: () => import("@/views/auth/ForceLogin.vue"),
			meta: {
				layout: "auth"
			}
		},
		{
			path: "/auth/2fa",
			name: "auth.2fa",
			component: () => import("@/views/auth/TwoFactorAuth.vue"),
			props: true,
			meta: {
				layout: "auth"
			}
		},
		{
			path: "/auth/reset-password",
			name: "auth.reset-password",
			component: () => import("@/views/auth/ResetPassword.vue"),
			meta: {
				layout: "auth"
			}
		},
		{
			path: "/auth/change-password/:token",
			name: "auth.change-password",
			component: () => import("@/views/auth/ChangePassword.vue"),
			meta: {
				layout: "auth"
			}
		},
		{
			path: "/auth/unauthorized",
			name: "auth.unauthorized",
			component: () => import("@/views/auth/Unauthorized.vue"),
			meta: {
				layout: "auth"
			}
		},
		{
			path: "/auth/send-password-reset",
			name: "auth.send-password-reset",
			component: () => import("@/views/auth/SendPasswordReset.vue"),
			meta: {
				layout: "auth"
			}
		},
		{
			path: "*",
			name: "404",
			component: () => import("@/views/auth/404.vue"),
			meta: {
				layout: "auth"
			}
		}
	]
});

router.beforeEach(async (to, from, next) => {
	Vue.bus.$emit("toggle-search", false); // Close search

	// check user is authenticated
	if (to.meta.layout !== "auth") {
		try {
			await window.apolloClient.query({
				query: UserQuery
			});
		} catch (error) {
			return next("/auth/login");
		}
	}

	// middleware pipeline
	const middleware = [intercom, permissions];

	const context = {
		to,
		from,
		next
	};

	return middleware[0]({
		...context,
		next: pipeline(context, middleware, 1)
	});
});

export default router;
